import type { AuditEvent } from "@payezy/api-client/admin";
import { searchAuditOptions } from "@payezy/api-client/admin/query";
import { Badge, Button, Card, Dialog, EmptyState, formatDate, Input, PageHeader, Select, shortId, Table, TD, TH, THead, TR } from "@payezy/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";

export function AuditPage() {
  const search = useSearch({ from: "/app/audit" });
  const navigate = useNavigate({ from: "/audit" });
  const [before, setBefore] = useState<string[]>([]);
  const q = useQuery({
    ...searchAuditOptions({
      query: {
        org_id: search.org_id || undefined,
        actor_type: search.actor_type as "user" | "staff" | "system" | "partner" | undefined,
        actor_id: search.actor_id || undefined,
        action: search.action || undefined,
        resource_id: search.resource_id || undefined,
        before: before.at(-1),
        limit: 100,
      },
    }),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<AuditEvent | null>(null);
  const items = q.data?.items ?? [];
  const set = (k: string, v: string) => {
    setBefore([]);
    void navigate({ search: (s) => ({ ...s, [k]: v || undefined }) });
  };
  return (
    <>
      <PageHeader title="Audit log" description="Append-only record of every change, including staff actions, view-as sessions and system events. Sensitive fields are redacted at write time." />
      <div className="mb-4 grid grid-cols-5 gap-3">
        <Select value={search.actor_type ?? ""} onChange={(e) => set("actor_type", e.target.value)}>
          <option value="">Any actor</option>
          {["user", "staff", "system", "partner"].map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Input placeholder="Actor id" defaultValue={search.actor_id} onBlur={(e) => set("actor_id", e.target.value.trim())} />
        <Input placeholder="Action prefix, e.g. payment." defaultValue={search.action} onBlur={(e) => set("action", e.target.value.trim())} />
        <Input placeholder="Organization id" defaultValue={search.org_id} onBlur={(e) => set("org_id", e.target.value.trim())} />
        <Input placeholder="Resource id" defaultValue={search.resource_id} onBlur={(e) => set("resource_id", e.target.value.trim())} />
      </div>
      <Card>
        <Table>
          <THead><TR><TH>When</TH><TH>Actor</TH><TH>Action</TH><TH>Resource</TH><TH>Organization</TH><TH>Reason</TH></TR></THead>
          <tbody>
            {items.map((e) => (
              <TR key={e.id} className="cursor-pointer hover:bg-ink-50" onClick={() => setOpen(e)}>
                <TD className="whitespace-nowrap">{formatDate(e.occurred_at, true)}</TD>
                <TD>
                  <Badge tone={e.actor_type === "staff" ? "warning" : "neutral"}>{e.actor_type}</Badge> <span className="font-mono text-xs">{shortId(e.actor_id)}</span>
                  {e.impersonation_id && <Badge tone="danger" className="ml-1">view-as</Badge>}
                </TD>
                <TD className="font-mono text-xs">{e.action}</TD>
                <TD className="text-xs">{e.resource_type} <span className="font-mono">{shortId(e.resource_id)}</span></TD>
                <TD className="font-mono text-xs">{e.org_id ? shortId(e.org_id) : "—"}</TD>
                <TD className="max-w-72 truncate text-sm" title={e.reason}>{e.reason || "—"}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
        {!q.isLoading && items.length === 0 && <EmptyState title="No events match" />}
        <div className="flex justify-end gap-2 border-t border-ink-100 px-5 py-3">
          <Button size="sm" variant="secondary" disabled={before.length === 0} onClick={() => setBefore((b) => b.slice(0, -1))}>Newer</Button>
          <Button size="sm" variant="secondary" disabled={items.length < 100} onClick={() => setBefore((b) => [...b, items.at(-1)!.occurred_at])}>Older</Button>
        </div>
      </Card>
      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(null)} title={open.action} description={formatDate(open.occurred_at, true)} wide>
          <pre className="max-h-[60vh] overflow-auto rounded-lg bg-ink-50 p-3 text-xs">{JSON.stringify(open, null, 2)}</pre>
        </Dialog>
      )}
    </>
  );
}
